import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useActions } from '../../../hooks/useActions';
import { useProcessing } from '../../../hooks/useProcessing';
import { IMemberListItem } from '../../../models/MemberListItem';
import { IPerson } from '../../../models/Person';
import { RoutePath } from '../../../models/enums/RoutePath';
import { updateModalState } from '../../../store/redux-slices/modals';
import { MEMBERS_LOADING_PROCESSING } from '../../../store/redux-slices/processes/constants';
import { EDIT_MEMBER_MODAL_NAME } from '../../../styledComponents/Modals/EditMemberModal/editMemberModal.const';
import { textContainsWords } from '../../../utils/textHelpers';
import { MemberOverviewPageParamsType } from '../../../models/types/RoutePath';
import { useNavigateSearch } from '../../../hooks/useNavigateSearch';
import { RequiredQueryParams } from './membersKidnapAndExtortion.models';
import { loadKEMembers } from '../../../store/saga-slices/membership/kidnapAndExtortion';
import {
  clearKEMembers,
  updateKEMembersList,
} from '../../../store/redux-slices/membership/kidnapAndExtortion';
import { ILoadKEMembersPayload } from '../../../store/saga-slices/membership/kidnapAndExtortion/models';
import { pageListKEMembersSelector } from '../../../store/redux-slices/membership/kidnapAndExtortion/selectors';
import { keLoadStandardPricing } from '../../../store/saga-slices/pricing/kidnapAndExtortion';
import { clearKEMembershipPlansPrices } from '../../../store/redux-slices/pricing/kidnapAndExtortion';
import { keMembershipPlansPricesSelector } from '../../../store/redux-slices/pricing/kidnapAndExtortion/selectors';

export const useMembersKidnapAndExtortion = () => {
  const LoadMembers = useActions(loadKEMembers);
  const ClearMembers = useActions(clearKEMembers);
  const location = useLocation();
  const navigate = useNavigateSearch();
  const pageListMembers = useSelector(pageListKEMembersSelector);
  const keMembershipPricing = useSelector(keMembershipPlansPricesSelector);

  const membersLoading = useProcessing(MEMBERS_LOADING_PROCESSING);

  const LoadStandardPricing = useActions(keLoadStandardPricing);
  const ClearStandardPricing = useActions(clearKEMembershipPlansPrices);

  const UpdateModalState = useActions(updateModalState);
  const UpdateMembersList = useActions(updateKEMembersList);

  useEffect(() => {
    LoadStandardPricing();
  }, [LoadStandardPricing]);

  const loadMembersHandler = useCallback(() => {
    const params = location.search.slice(1);

    if (!textContainsWords(params, RequiredQueryParams)) {
      return;
    }

    const payload: ILoadKEMembersPayload = {
      params,
    };

    LoadMembers(payload);
  }, [LoadMembers, location.search]);

  useEffect(() => loadMembersHandler(), [loadMembersHandler]);

  useEffect(
    () => () => {
      ClearMembers();
      ClearStandardPricing();
    },
    [ClearMembers, ClearStandardPricing],
  );
  useEffect(() => () => ClearMembers(), [ClearMembers]);

  const onRowClickHandler = useCallback(
    (row: IMemberListItem) => {
      const params: MemberOverviewPageParamsType = {
        memberId: row.personId,
      };
      navigate(RoutePath.MemberOverviewPage, { params });
    },
    [navigate],
  );

  const rowEditHandler = useCallback(
    (id: string) => {
      UpdateModalState([EDIT_MEMBER_MODAL_NAME, id]);
    },
    [UpdateModalState],
  );

  const onMemberEditedHandler = useCallback(
    (person: IPerson) => {
      UpdateMembersList(person);
    },
    [UpdateMembersList],
  );

  return {
    membersLoading,
    pageListMembers,
    onRowClickHandler,
    rowEditHandler,
    onMemberEditedHandler,
    keMembershipPricing,
  };
};
